/**
 * STORE-SPLIT slice 4 — the revision-diff / document-table-fingerprint helper
 * family, extracted VERBATIM from `editorStore.ts` (no behaviour change; same
 * STORE-SPLIT lineage as slices 1-3). These are PURE functions (no zustand
 * `set`/`get`, no `useEditorStore`, no module-mutable state) — the ABB-S1
 * revision-classification math the dispatch chokepoint + the undo/redo/abort
 * lifecycle both consume to decide WHICH bump-classes a committed state delta
 * touched (geometry / table / context / selection).
 *
 * `editorStore.ts` imports every function back and RE-EXPORTS `docHeaderChanged`
 * (its former public export) so external import paths stay unchanged. This
 * module imports ONLY leaf helpers (drawing-model, contracts, ./types,
 * ./geometryMutationHelpers) — none import editorStore.ts, so the deps graph
 * stays acyclic (verified by `npm run deps:graph`).
 *
 * ## Function dependency map
 *
 *   computeRevisionStateDiff (the ABB-S1 G1 single source of truth)
 *     -> diffDocumentMutationsHasEntityChange -> diffDocumentMutations /
 *        documentWithoutSyntheticSpaces (geometryMutationHelpers.ts)
 *     -> shellEntitiesChanged (EntityPreview row diff — CHPROP/MATCHPROP)
 *     -> docHeaderChanged -> documentTableFingerprint -> stableJson /
 *        omitSpaceEntitiesWithoutReading
 *     -> contextChangedBetween (doc + shell activeSpaceId / layoutContext)
 *     -> selectionMembershipChanged (drawing-model)
 *   isEmptyMutation (history-record no-op guard) — standalone predicate.
 */
import {
  selectionMembershipChanged,
  type DrawingDocument,
  type RevisionStateDiff,
} from "@dxf-ai-editor/drawing-model";
import {
  type EditorShellState,
  type EntityPreview,
} from "@dxf-ai-editor/contracts";
import {
  diffDocumentMutations,
  documentWithoutSyntheticSpaces,
  SYNTHETIC_EDIT_SPACE_KINDS,
} from "./geometryMutationHelpers";
import { type EntityMutation } from "./types";

export function isEmptyMutation(m: EntityMutation): boolean {
  return (
    m.added.length === 0 &&
    m.updated.length === 0 &&
    m.removed.length === 0 &&
    m.styleTablesTouched.size === 0
  );
}

/**
 * ABB-S1 round-4 GENERALIZATION 1 — stable, recursive, key-ORDER-INSENSITIVE
 * JSON of an arbitrary value. The convergent oracle for the table-class diff:
 * a Zod re-parse (`assembleDrawingDocument`) rebuilds every nested object — and
 * a reducer `{...doc, field}` re-spread re-orders keys — so a plain
 * `JSON.stringify` value compare flaps on incidental key order. Sorting keys
 * recursively makes the serialisation a faithful VALUE compare that survives the
 * re-parse / re-spread (the same reason `canonicalRecordJson` sorted keys, now
 * generalised to the whole document). `undefined` members are omitted by
 * `JSON.stringify` consistently on both sides (a Zod `.default` materialises the
 * field on both, so the omit is symmetric).
 */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(obj).sort()) sorted[k] = obj[k];
      return sorted;
    }
    return val;
  });
}

/**
 * ABB-S1 round-4 GENERALIZATION 1 — the document "table-class fingerprint": a
 * stable serialisation of EVERYTHING in the document EXCEPT the two axes that
 * are NOT table-class:
 *   - each space's drawable `entities` array (that is the GEOMETRY class — its
 *     own diff at the chokepoint / `diffDocumentMutations`); dropped ENTIRELY so
 *     NO entity add/remove/update (which all shift the array, incl its length)
 *     registers as a table change here.
 *   - the document `activeSpaceId` (that is the CONTEXT class — a space SWITCH,
 *     handled by the context diff; a space ADD/REMOVE/RENAME still registers via
 *     the spaces' id/name/kind which ARE kept).
 *
 * Everything else — styleTables, layers, ALL header sysvars (cannoscale /
 * insunits / cecolor / osmode / ltscale / pdmode / …), layouts, units, plot
 * settings, `blocks` (incl `blocks[name].dynamicMetadata` — the round-4 F1 dynamic-
 * block authoring miss — AND `blocks[name].entities` for BEDIT ATTDEF authoring),
 * `blockRecords` metadata, `xrefRegistry`, and any FUTURE non-entity field — is
 * folded into the fingerprint with NO per-field enumeration. A document change
 * that survives stripping the geometry + context axes IS a table change, by
 * construction; nothing to keep adding per new field.
 */
/**
 * PR #362 round-5 (F1 ROOT) — copy a space's own enumerable properties EXCEPT
 * `entities`, WITHOUT ever reading the `entities` property. A read-through
 * wrapper (`wrapSpaceAsReadThrough`) overlays `entities` as a getter that
 * materialises the full per-space array from the compact cache on access; a
 * `{ entities, ...rest }` destructure (or any property read) would fire that
 * getter and trigger an O(N) materialisation just to discard the value.
 *
 * `Object.keys` enumerates the own keys (incl the accessor `entities`) WITHOUT
 * invoking any getter; we skip `entities` and copy every other key by value —
 * so the table fingerprint of a cache-backed document materialises nothing.
 */
export function omitSpaceEntitiesWithoutReading(
  space: DrawingDocument["spaces"][number],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // `Object.keys` enumerates own keys (incl the accessor `entities`) WITHOUT
  // invoking any getter; copy every key BY VALUE except `entities`. The result
  // feeds `stableJson` (serialises any shape), so a plain record is sufficient
  // and side-steps the discriminated-union narrowing the prior destructure had.
  for (const key of Object.keys(space)) {
    if (key === "entities") continue;
    out[key] = (space as unknown as Record<string, unknown>)[key];
  }
  return out;
}

export function documentTableFingerprint(doc: DrawingDocument): string {
  const { activeSpaceId: _activeSpaceId, spaces, ...rest } = doc;
  // Reconcile with the CONTEXT axis (round-4 F4): entering/exiting BEDIT/REFEDIT
  // ADDS/REMOVES a SYNTHETIC edit space (`block-edit`/`block-test`/`refedit`)
  // AND switches `activeSpaceId`. That is a CONTEXT switch (caught by the context
  // diff), NOT a named-object-table topology change — so synthetic edit spaces
  // are EXCLUDED from the table fingerprint to avoid double-classifying enter/
  // exit as both context AND table. The block-DEFINITION content a BEDIT session
  // actually writes lands in `blocks[name].entities` (kept in `rest`), so a real
  // block edit (incl `createIfMissing`) still bumps table; only the synthetic
  // session space's existence is context-only. The PERSISTENT layout-table spaces
  // (`model`/`layout`) stay in — LAYOUT NEW/DELETE/RENAME is a table change.
  const spacesStructural = spaces
    .filter((space) => space.kind === "model" || space.kind === "layout")
    .map((space) => {
      // Drop the drawable `entities` array ENTIRELY (the geometry axis): an
      // entity add/remove/update shifts the array (and its length), so keeping
      // any entity-derived value would mis-register a geometry edit as a table
      // change. The retained id/name/kind register a space add/remove/rename/
      // kind change (layout-table topology).
      //
      // PR #362 round-5 (F1 ROOT) — the space may be a COMPACT-CACHE read-through
      // wrapper (`wrapSpaceAsReadThrough`) whose `entities` is a LAZY getter that
      // materialises the whole space from the live cache on access. The prior
      // `const { entities: _entities, ...spaceRest } = space` DESTRUCTURE READ
      // `space.entities` to bind `_entities` (destructuring a key invokes its
      // getter), firing a full O(N) materialisation on EVERY fingerprint — even
      // though the value is immediately discarded. Build `spaceRest` by COPYING
      // the own enumerable keys EXCEPT `entities`, never reading the getter, so
      // fingerprinting a cache-backed doc materialises NOTHING.
      return omitSpaceEntitiesWithoutReading(space);
    });
  return stableJson({ ...rest, spaces: spacesStructural });
}

/**
 * ABB-S1 round-4 GENERALIZATION 1 — TABLE-class detector, GENERALISED from the
 * prior per-field enumeration (cannoscale / insunits / cecolor / block COUNT /
 * `blockDefinitionEntitiesChanged` / `xrefOrBlockMetadataChanged` / …) to ONE
 * rule: the document changed in something OTHER than the space-entity arrays and
 * the active-space CONTEXT.
 *
 * Root of the long-tail @codex churn (round-3 block-rename, earlier xref /
 * block-record metadata, round-4 `blocks[name].dynamicMetadata`): each round
 * enumerated one more sub-field the table diff was missing. Enumerating
 * sub-fields can never converge — a new typed document field re-opens the gap.
 * The generalisation closes the CLASS: by comparing the document MINUS the
 * geometry axis (space entities) MINUS the context axis (activeSpaceId), ANY
 * other structural/ref change (present OR future) is a table change with no new
 * code. `docMutated` (history-record gate) and the chokepoint `tableChanged`
 * both consume this, so block-DEFINITION authoring, header sysvars, layout
 * topology, plot settings, xref, units AND dynamicMetadata all record + bump.
 *
 * Short-circuits on reference equality (the hot no-op path); the
 * fingerprint is computed only when the document reference actually changed.
 */
export function docHeaderChanged(
  prev: DrawingDocument,
  next: DrawingDocument,
): boolean {
  if (prev === next) return false;
  return documentTableFingerprint(prev) !== documentTableFingerprint(next);
}

/**
 * ABB-S1 G1/G3 #4 — detect a SHELL-store entity-row change between two
 * `EditorShellState.drawing.entities` (EntityPreview) arrays. CHPROP / MATCHPROP /
 * Properties property-and-metadata edits flow through `reducePropertyCommand`,
 * which mutates the SHELL preview store (`state.drawing.entities`) — NOT the
 * geometry document — so `diffDocumentMutations` (geometryDocument-only) is BLIND
 * to them and the agent never sees a geometry bump for a property edit (G3 #4).
 * This predicate compares the preview rows by id-set + per-id JSON shape (the
 * EntityPreview property bag is a flat string map, so JSON is a faithful value
 * compare that survives a Zod re-parse / object re-spread). O(N) preview rows,
 * short-circuited on reference equality.
 */
export function shellEntitiesChanged(
  prev: readonly EntityPreview[],
  next: readonly EntityPreview[],
): boolean {
  if (prev === next) return false;
  if (prev.length !== next.length) return true;
  const prevById = new Map<string, EntityPreview>();
  for (const e of prev) prevById.set(e.id, e);
  for (const e of next) {
    const before = prevById.get(e.id);
    if (!before) return true; // a new id appeared (length-equal ⇒ id reshuffle)
    if (before === e) continue; // reference-equal → unchanged
    if (JSON.stringify(before) !== JSON.stringify(e)) return true; // edited row
  }
  return false;
}

/**
 * #518 (Codex P1 follow-up) — the P1 fix forces `syncShellStateFromGeometryDocument`
 * to do a genuine full rebuild on a geo-authoritative space switch (so the
 * newly-active synthetic space's own entities actually reach
 * `state.drawing.entities` — otherwise the Block Editor opens with its
 * contents invisible/unselectable). That rebuild changes the SHAPE of
 * `state.drawing.entities` whenever a synthetic space's PRESENCE flips
 * (enter: absent→present, exit: present→absent), which would make
 * `shellEntitiesChanged` see a spurious geometry-class delta for a
 * BEDIT/TESTBLOCK/REFEDIT enter/exit/abort — exactly the class of false
 * positive `diffDocumentMutationsHasEntityChange`'s
 * `documentWithoutSyntheticSpaces` exclusion already prevents on the
 * geometry-document side (PR #362 round-5/6 F5).
 *
 * Deliberately narrower than a blanket "always strip every synthetic-space
 * row": a synthetic space that is STABLY present on BOTH sides of the diff
 * (an ordinary geometry edit issued WHILE already inside an open BEDIT/
 * REFEDIT session — undo/redo included) must NOT be excluded, or the shell-
 * entities diff — the ONLY signal left once the doc-level diff already
 * excludes synthetic spaces unconditionally — goes blind to that real edit.
 * Only spaces whose PRESENCE differs between `prevDoc`/`nextDoc` (a genuine
 * lifecycle transition, including one that PERSISTS past a null-inverse
 * abort) are stripped from BOTH entity lists before comparing.
 */
function transitionalSyntheticSpaceIds(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
): ReadonlySet<string> {
  const prevIds = new Set(
    prevDoc.spaces
      .filter((s) => SYNTHETIC_EDIT_SPACE_KINDS.has(s.kind))
      .map((s) => s.id),
  );
  const nextIds = new Set(
    nextDoc.spaces
      .filter((s) => SYNTHETIC_EDIT_SPACE_KINDS.has(s.kind))
      .map((s) => s.id),
  );
  const transitional = new Set<string>();
  for (const id of prevIds) if (!nextIds.has(id)) transitional.add(id);
  for (const id of nextIds) if (!prevIds.has(id)) transitional.add(id);
  return transitional;
}

export function shellEntitiesWithoutTransitionalSyntheticSpaces(
  prevEntities: readonly EntityPreview[],
  nextEntities: readonly EntityPreview[],
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
): {
  prev: readonly EntityPreview[];
  next: readonly EntityPreview[];
} {
  const transitional = transitionalSyntheticSpaceIds(prevDoc, nextDoc);
  if (transitional.size === 0) {
    return { prev: prevEntities, next: nextEntities };
  }
  return {
    prev: prevEntities.filter((e) => !transitional.has(e.spaceId)),
    next: nextEntities.filter((e) => !transitional.has(e.spaceId)),
  };
}

/**
 * ABB-S1 round-4 F4 — the CONTEXT-class detector: the editing context changed iff
 * the active EDITING SPACE or the layout-context ("model"/"paper") differs — on
 * EITHER the shell side OR the geometryDocument side.
 *
 * The prior diff compared ONLY the shell `state.drawing.activeSpaceId/
 * layoutContext`. BEDIT/REFEDIT lifecycle commands switch
 * `geometryDocument.activeSpaceId` to a synthetic edit space while
 * `syncShellStateFromGeometryDocument` PRESERVES the previous shell active space
 * when it still exists — so entering an existing (e.g. empty) block changes the
 * real editing context with NO entity/table delta AND NO shell active-space
 * change, leaving `contextRevision` stale (an agent's context precondition
 * falsely passes). Comparing the DOC's `activeSpaceId` too closes that: a doc-
 * side space SWITCH bumps context. This is the CONTEXT half of the GENERALIZATION-
 * 1 reconciliation — `activeSpaceId` is CONTEXT (a switch), never table; the
 * table fingerprint EXCLUDES `activeSpaceId` + the synthetic edit spaces so the
 * switch is not double-classified.
 */
export function contextChangedBetween(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  prevState: EditorShellState,
  nextState: EditorShellState,
): boolean {
  return (
    prevDoc.activeSpaceId !== nextDoc.activeSpaceId ||
    prevState.drawing.activeSpaceId !== nextState.drawing.activeSpaceId ||
    prevState.drawing.layoutContext !== nextState.drawing.layoutContext
  );
}

/**
 * ABB-S1 G1 (STRUCTURAL ROOT) — the SINGLE source of truth for "what bump-class
 * set did a committed mutation touch", derived from the ACTUAL pre/post state
 * delta (NOT the command kind). Used by BOTH the universal dispatch chokepoint
 * AND the undo/redo lifecycle: the inner `__bypassHistory` dispatch that applies
 * the inverse/forward produces the real state change, so capturing the pre/post
 * store snapshot around it and diffing the NET delta classifies undo/redo/replay
 * EXACTLY like the forward path — eliminating the kind-classifier
 * (`bumpClassesForCommittedCommand`) and its whole edge sub-class (redo-by-kind,
 * ATTDEF mis-class, nested-replay recursion). Nested replay-inverses naturally
 * fold because we diff the NET state change, not each member.
 *
 * Covers ALL semantic slices (the G3 + convergence-audit completeness set):
 *   geometryDocument.entities (diffDocumentMutations), state.drawing.entities
 *   (shellEntitiesChanged — CHPROP/MATCHPROP), the whole document MINUS the
 *   geometry + context axes (docHeaderChanged → documentTableFingerprint, incl
 *   styleTables / LAYER table / blocks incl dynamicMetadata / blockRecords /
 *   xrefRegistry / layouts / units / plot), doc+shell activeSpaceId + layout-
 *   context (contextChangedBetween), selection ids.
 *
 * NOTE (#518 Codex P1 follow-up) — the shell-entities half of the diff is run
 * through {@link shellEntitiesWithoutTransitionalSyntheticSpaces}, NOT a
 * blanket "strip every synthetic-space row": a batch whose ONLY command is a
 * null-inverse `space/enter-block-edit` (e.g. abort — the space PERSISTS with
 * no inverse to undo it) makes the synthetic space's PRESENCE itself the net
 * change (absent in the pre-batch base doc, present in the post-abort doc) —
 * a lifecycle artifact, not a user geometry edit, so it must be excluded, the
 * same class of false positive `documentWithoutSyntheticSpaces` already
 * prevents on the doc-diff side. But a synthetic space STABLY present on BOTH
 * sides (undo/redo of an ordinary edit issued WHILE already inside an open
 * BEDIT/REFEDIT session) is NOT excluded — it is the ONLY remaining signal
 * once the doc-diff excludes its space unconditionally, so blinding it there
 * too would make undo/redo of in-session edits stop bumping geometry (see
 * `revisionCounters.live.test.ts`'s "undo/redo of a REAL geometry edit inside
 * a block-edit session still bumps geometry").
 */
export function computeRevisionStateDiff(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  prevState: EditorShellState,
  nextState: EditorShellState,
): RevisionStateDiff {
  const filteredShellEntities = shellEntitiesWithoutTransitionalSyntheticSpaces(
    prevState.drawing.entities,
    nextState.drawing.entities,
    prevDoc,
    nextDoc,
  );
  const entityStateChanged =
    diffDocumentMutationsHasEntityChange(prevDoc, nextDoc) ||
    shellEntitiesChanged(filteredShellEntities.prev, filteredShellEntities.next);
  const tableChanged = docHeaderChanged(prevDoc, nextDoc);
  const contextChanged = contextChangedBetween(
    prevDoc,
    nextDoc,
    prevState,
    nextState,
  );
  const selectionChanged = selectionMembershipChanged(
    prevState.selection.entityIds,
    nextState.selection.entityIds,
  );
  return { entityStateChanged, tableChanged, contextChanged, selectionChanged };
}

/**
 * ABB-S1 G1 — entity-STATE delta between two whole documents (the undo/redo path
 * has no `commitMutation` `EntityMutation` in hand, only pre/post documents).
 * `diffDocumentMutations` also surfaces a `styleTablesTouched` flag, but for the
 * lifecycle path the TABLE class is already covered by `docHeaderChanged`
 * (the document-table fingerprint); here we want ONLY the add/remove/update
 * entity signal, so we read those three slices.
 *
 * PR #362 round-6 (F5) — EXCLUDE the SYNTHETIC, TRANSIENT BEDIT/REFEDIT edit
 * spaces (`block-edit`/`block-test`/`refedit`) from BOTH sides of the diff, the
 * SAME exclusion the forward `space/*` reducer diff (`geometryReducer.ts`) and
 * the TABLE fingerprint (`documentTableFingerprint`) apply. Without it, an
 * ABORTED batch containing a `space/enter-block-edit`/REFEDIT context switch
 * (which has NO inverse and SURVIVES the abort) would make the net-diff count the
 * synthetic edit-space's COPIED entities as added/removed → a spurious
 * `geometryRevision` bump (and inverted per-class correctness) for a CONTEXT-only
 * abort. A REAL entity edit INSIDE a block-edit session is a `geometry/*` command
 * that lands in the synthetic space AND mirrors into `blocks[name].entities` — so
 * it still bumps geometry; only the synthetic space's copied-entities artifact is
 * excluded here. Same fix on the undo, redo AND abort paths (all route through
 * `computeRevisionStateDiff`).
 */
export function diffDocumentMutationsHasEntityChange(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
): boolean {
  if (prevDoc === nextDoc) return false;
  const m = diffDocumentMutations(
    documentWithoutSyntheticSpaces(prevDoc),
    documentWithoutSyntheticSpaces(nextDoc),
  );
  return m.added.length > 0 || m.removed.length > 0 || m.updated.length > 0;
}
